import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import axios from "axios";
import "./CommentsDrawer.css";

export default function CommentsDrawer({ post, user, onClose }) {
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/getComments/${post._id}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setComments(data || []);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  }, [post._id, user.token]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    try {
      setSubmitting(true);
      const { data } = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/comment`,
        {
          postId: post._id,
          comment: commentText,
          image: "",
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      setComments([data, ...comments]);
      setCommentText("");
      setReplyTo(null);
    } catch (error) {
      console.error("Error posting comment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/likeComment/${commentId}`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      setComments(comments.map(c => 
        c._id === commentId 
          ? { ...c, likes: c.likes?.includes(user.id) 
              ? c.likes.filter(id => id !== user.id)
              : [...(c.likes || []), user.id]
            }
          : c
      ));
    } catch (error) {
      console.error("Error liking comment:", error);
    }
  };

  return (
    <div className="comments_drawer_overlay" onClick={onClose}>
      <div className="comments_drawer" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="comments_drawer_header">
          <h3>Comentários</h3>
          <button onClick={onClose} className="comments_drawer_close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        {/* Comments List */}
        <div className="comments_drawer_list">
          {loading ? (
            <div className="comments_loading">
              <div className="spinner"></div>
            </div>
          ) : comments.length === 0 ? (
            <div className="comments_empty">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
              </svg>
              <p>Nenhum comentário ainda</p>
              <span>Seja o primeiro a comentar!</span>
            </div>
          ) : (
            comments.map((comment) => (
              <div key={comment._id} className="comment_item">
                <Link to={`/profile/${comment.commentBy?.username}`} className="comment_avatar">
                  <img src={comment.commentBy?.picture} alt="" />
                </Link>
                <div className="comment_content">
                  <div className="comment_header">
                    <Link to={`/profile/${comment.commentBy?.username}`} className="comment_username">
                      {comment.commentBy?.first_name} {comment.commentBy?.last_name}
                    </Link>
                    <span className="comment_time">
                      <Moment fromNow locale="pt-br">{comment.commentAt}</Moment>
                    </span>
                  </div>
                  <p className="comment_text">{comment.comment}</p>
                  {comment.image && (
                    <img src={comment.image} alt="" className="comment_image" />
                  )}
                  <div className="comment_actions">
                    <button 
                      onClick={() => handleLikeComment(comment._id)}
                      className={`comment_action_btn ${comment.likes?.includes(user.id) ? 'liked' : ''}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill={comment.likes?.includes(user.id) ? "#f00" : "none"} stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                      {comment.likes?.length > 0 && <span>{comment.likes.length}</span>}
                    </button>
                    <button 
                      onClick={() => setReplyTo(comment)}
                      className="comment_action_btn"
                    >
                      Responder
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Comment Input */}
        <form onSubmit={handleSubmitComment} className="comments_drawer_input">
          {replyTo && (
            <div className="reply_indicator">
              <span>Respondendo a @{replyTo.commentBy?.username}</span>
              <button type="button" onClick={() => setReplyTo(null)}>✕</button>
            </div>
          )}
          <img src={user.picture} alt="" className="comment_input_avatar" />
          <input
            type="text"
            placeholder="Adicione um comentário..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            disabled={submitting}
          />
          <button 
            type="submit" 
            disabled={!commentText.trim() || submitting}
            className="comment_submit_btn"
          >
            {submitting ? (
              <div className="spinner_small"></div>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
